import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

import type {
  CppAvailableAdGroup,
  CppBatchCreateTestRequest,
  CppCreateTestRequest,
  CppEstimateResponse,
  CppTestDetail,
  CppTestListItem,
} from "./types";

export function useCppTests(scope: "active" | "history") {
  return useQuery({
    queryKey: ["cpp-tests", scope],
    queryFn: async () => {
      const { data } = await api.get<{ rows: CppTestListItem[] }>(
        `/api/cpp-tests?scope=${scope}`,
      );
      return data.rows;
    },
    // Live tests update server-side every ~15-30 min; poll while open.
    refetchInterval: scope === "active" ? 60_000 : false,
    staleTime: 30_000,
  });
}

export function useCppTestDetail(testId: number | null) {
  return useQuery({
    queryKey: ["cpp-test", testId],
    enabled: testId != null,
    queryFn: async () => {
      const { data } = await api.get<CppTestDetail>(
        `/api/cpp-tests/${testId}`,
      );
      return data;
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useCppAvailableAdGroups(campaignId: number | null) {
  return useQuery({
    queryKey: ["cpp-adgroups", campaignId],
    enabled: campaignId != null,
    queryFn: async () => {
      const { data } = await api.get<{ rows: CppAvailableAdGroup[] }>(
        `/api/cpp-tests/adgroups?campaign_id=${campaignId}`,
      );
      return data.rows;
    },
    staleTime: 30_000,
  });
}

export function useCreateCppTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CppCreateTestRequest) => {
      const { data } = await api.post<{ test_id: number }>(
        "/api/cpp-tests",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cpp-tests"] });
    },
  });
}

export function useCreateCppTestBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CppBatchCreateTestRequest) => {
      const { data } = await api.post<{
        tests: { campaign_id: number; test_id: number; name: string }[];
      }>("/api/cpp-tests/batch", payload);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cpp-tests"] });
    },
  });
}

export function useEstimateCppDuration() {
  return useMutation({
    mutationFn: async (payload: {
      campaign_id: number;
      ad_group_id: number;
      variant_count: number;
      metric: string;
      min_uplift_pct: number;
      confidence_level: number;
    }) => {
      const { data } = await api.post<CppEstimateResponse>(
        "/api/cpp-tests/estimate-duration",
        payload,
      );
      return data;
    },
  });
}

function useTestAction(path: (id: number) => string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (testId: number) => {
      await api.post(path(testId));
      return testId;
    },
    onSuccess: (testId) => {
      void qc.invalidateQueries({ queryKey: ["cpp-tests"] });
      void qc.invalidateQueries({ queryKey: ["cpp-test", testId] });
    },
  });
}

export function usePauseCppTest() {
  return useTestAction((id) => `/api/cpp-tests/${id}/pause`);
}
export function useResumeCppTest() {
  return useTestAction((id) => `/api/cpp-tests/${id}/resume`);
}
export function useStopCppTest() {
  return useTestAction((id) => `/api/cpp-tests/${id}/stop`);
}

// --- per-app column presets (results table) --------------------------------

export type CppColumnPreset = {
  id: number;
  name: string;
  columns: string[];
  widths: Record<string, number>;
  is_default: boolean;
};

export function useCppColumnPresets(appId: number | null) {
  return useQuery({
    queryKey: ["cpp-col-presets", appId],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<{ rows: CppColumnPreset[] }>(
        `/api/cpp-tests/column-presets?app_id=${appId}`,
      );
      return data.rows;
    },
    staleTime: 60_000,
  });
}

export function useCreateCppColumnPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { app_id: number; name: string; columns: string[] }) => {
      const { data } = await api.post<{ id: number }>(
        "/api/cpp-tests/column-presets",
        p,
      );
      return data;
    },
    onSuccess: (_d, p) =>
      void qc.invalidateQueries({ queryKey: ["cpp-col-presets", p.app_id] }),
  });
}

export function useUpdateCppColumnPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { id: number; app_id: number; columns: string[] }) => {
      await api.put(`/api/cpp-tests/column-presets/${p.id}`, {
        columns: p.columns,
        widths: {},
      });
      return p;
    },
    onSuccess: (p) =>
      void qc.invalidateQueries({ queryKey: ["cpp-col-presets", p.app_id] }),
  });
}

export function useDeleteCppColumnPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { id: number; app_id: number }) => {
      await api.delete(`/api/cpp-tests/column-presets/${p.id}`);
      return p;
    },
    onSuccess: (p) =>
      void qc.invalidateQueries({ queryKey: ["cpp-col-presets", p.app_id] }),
  });
}

export function useSetDefaultCppColumnPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { id: number; app_id: number }) => {
      await api.post(`/api/cpp-tests/column-presets/${p.id}/default`);
      return p;
    },
    onSuccess: (p) =>
      void qc.invalidateQueries({ queryKey: ["cpp-col-presets", p.app_id] }),
  });
}

export function useClearDefaultCppColumnPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { id: number; app_id: number }) => {
      await api.delete(`/api/cpp-tests/column-presets/${p.id}/default`);
      return p;
    },
    onSuccess: (p) =>
      void qc.invalidateQueries({ queryKey: ["cpp-col-presets", p.app_id] }),
  });
}

export function useApplyCppWinner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      testId: number;
      groupId: number;
      variantId: number;
    }) => {
      await api.post(
        `/api/cpp-tests/${args.testId}/groups/${args.groupId}/apply`,
        { variant_id: args.variantId },
      );
      return args;
    },
    onSuccess: (args) => {
      void qc.invalidateQueries({ queryKey: ["cpp-tests"] });
      void qc.invalidateQueries({ queryKey: ["cpp-test", args.testId] });
    },
  });
}
